import axios from "axios";
import { defineStore } from "pinia";

export class MarketCartItem {
    id!: number;
    nom!: string;
    image?: string;
    montant!: number;
    quantite!: number;
    type_promotion?: string;
    taux_promotion?: number;
    valeur_promotion!: number;
    total_promotion!: number;
    montant_apres_remises!: number;
    mode_paiement!: string;
    montant_echeance!: number;
    nb_echeance!: number;
    total_echeance!: number;
    sous_total!: number;
    total!: number;
}

export class MarketTypeLivraison {
    id!: number;
    libelle!: string;
    montant!: number;
    isFree!: boolean;
}

export class MarketUserAdresse {
    id!: number;
    user_id!: number;
    libelle!: string;
    telephone!: string;
    adresse!: string;
    ville!: string;
    commune!: string;
}

export const useMarketCartStore = defineStore('marketCart', {
    state: () => ({
        items: [] as MarketCartItem[],
        fraisLivraison: null as MarketTypeLivraison | null,
        adresseLivraison: null as MarketUserAdresse | null,
    }),

    getters: {
        getSousTotal: (state) =>
            state.items.reduce((total, item) => total + item.sous_total, 0),

        getTotalPromotion: (state) => 
            state.items.filter(item => !!item.total_promotion).reduce((total, item) => total + item.total_promotion, 0),

        getTotalEcheances: (state) => 
            state.items.filter(item => !!item.total_echeance).reduce((total, item) => total + item.total_echeance, 0),

        getTotalFraisLivraison: (state) => {
            if (state.fraisLivraison && state.fraisLivraison.montant) {
                return state.fraisLivraison.montant;
            }
            return 0;
        },

        getTotal(): number {
            return this.getSousTotal - this.getTotalPromotion + this.getTotalFraisLivraison;
        },

        getTotalItems: (state) =>
            state.items.reduce((total, item) => total + item.quantite, 0),
    },

    actions: {
        async addToCart(product: Omit<MarketCartItem, 'quantite'>) {
            const clonedItems = [...this.items];
            const existing = clonedItems.find(i => i.id === product.id && i.mode_paiement === product.mode_paiement);
            if (existing) {
                existing.quantite++
            } else {
                clonedItems.push({ ...product, quantite: 1 })
            }

            if (!this._validateCartItems(clonedItems)) {
                throw "Tous les produits du panier doivent avoir le même mode de paiement.";
            }

            const calculatedItems = this._recalculateTotals(clonedItems);
            this.items = [...calculatedItems];
            await this._synCart();
        },

        _validateCartItems(items: MarketCartItem[]) {
            let cartIsValid = true;
            let mode_paiement = null;
            for (let i = 0; i < items.length; i++) {
                const item = items[i];
                if (mode_paiement && item.mode_paiement !== mode_paiement) {
                    cartIsValid = false;
                    break;
                }
                mode_paiement = item.mode_paiement;
            }
            return cartIsValid;
        },

        async removeFromCart(item_id: any) {
            const clonedItems = [...this.items];
            const index = clonedItems.findIndex(i => i.id === item_id)
            if (index > -1) {
                clonedItems.splice(index, 1);
            }
            const calculatedItems = this._recalculateTotals(clonedItems);
            this.items = [...calculatedItems];
            await this._synCart();
        },

        setQuantity(item_id: number, quantity: number) {
            const clonedItems = [...this.items];
            const index = clonedItems.findIndex(i => i.id == item_id);
            if (index > -1) {
                clonedItems[index].quantite = quantity;
                const calculatedItems = this._recalculateTotals(clonedItems);
                this.items = [...calculatedItems];
                this._synCart();
            }
        },

        clearCart() {
            this.items = [];
            this.fraisLivraison = null;
            this.adresseLivraison;
            this._synCart();
        },

        _recalculateTotals(items: MarketCartItem[]) {
            const updatedItems: MarketCartItem[] = [];
            items.forEach(item => {
                let totalPromotion = 0;
                const typePromotion = item.type_promotion;
                const sousTotal = item.montant * item.quantite;
                if (typePromotion == "PERCENTAGE" && item.valeur_promotion) {
                    totalPromotion = item.valeur_promotion * item.quantite;
                }
                const total = sousTotal - totalPromotion;
                const totalEcheance = item.montant_echeance * item.quantite;

                updatedItems.push({
                    id: item.id,
                    nom: item.nom,
                    montant: item.montant,
                    sous_total: sousTotal,
                    type_promotion: typePromotion,
                    taux_promotion: item.taux_promotion,
                    valeur_promotion: item.valeur_promotion,
                    total_promotion: totalPromotion,
                    montant_apres_remises: item.montant_apres_remises,
                    mode_paiement: item.mode_paiement,
                    montant_echeance: item.montant_echeance,
                    nb_echeance: item.nb_echeance,
                    total_echeance: totalEcheance,
                    total: total,
                    quantite: item.quantite,
                    image: item.image,
                })
            });
            return updatedItems;
        },

        async _fetchCart() {
            try {
                const cartItems = await axios.get(`/market/get_cart`);
                if (cartItems && cartItems.data && Array.isArray(cartItems.data)) {
                    const calculatedItems = this._recalculateTotals(cartItems.data);
                    this.items = [...calculatedItems];
                }
            } catch (error) {
                // Ignorer les erreurs de récupération du panier
            }
        },

        async _synCart() {
            // eslint-disable-next-line no-empty
            try { await axios.post(`/market/sync_cart`, { items: this.items }); } catch { }
        },
    }
})